import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonGroup } from 'react-bootstrap';
import FullPageSpinner from 'components/common/full-page-spinner';
import { OutputDataProvider, useOutputData } from '../output-performance/OutputDataContext';
import { useFactoryTotal } from '../output-performance/useStationData';
import { formatValue } from '../output-performance/outputPerformanceShared';
import { AreaSection } from '../output-performance/AreaCards';
import { OutputDetailModal } from '../output-performance/OutputDetailModal';

function FactoryFilter({ value, onChange, options }) {
  return (
    <ButtonGroup size="sm" className="output-factory-filter">
      {options.map(option => (
        <Button
          key={option.value}
          variant={value === option.value ? 'primary' : 'outline-primary'}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </ButtonGroup>
  );
}

FactoryFilter.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired
};

function MetadataMissingState() {
  return (
    <div className="output-metadata-missing">
      <strong>Metadata not available</strong>
      <span>
        Output Performance now reads structure from backend metadata only.
      </span>
    </div>
  );
}

function OutputPerformanceContent({ date }) {
  const { factories, factoryConfig, factoryId, setFactoryId, factoryOptions, loading } = useOutputData();
  const factoryTotal = useFactoryTotal(factoryConfig);
  const [selectedDetail, setSelectedDetail] = useState(null);

  useEffect(() => {
    setSelectedDetail(null);
  }, [factoryId]);

  if (!factories.length) {
    return <MetadataMissingState />;
  }

  return (
    <div className="output-performance-tab" style={{ position: 'relative' }}>
      <FullPageSpinner show={loading} quantity={3} />
      <div className="output-performance-header">
        <div>
          <h3>Output Performance</h3>
          <div className="output-performance-subtitle">
            {factoryConfig?.name} factory areas{date ? ` (${date})` : ''}
          </div>
        </div>
        <FactoryFilter
          value={factoryId}
          onChange={setFactoryId}
          options={factoryOptions}
        />
      </div>

      <div className="output-performance-total">
        <span>Total Output</span>
        <strong>{formatValue(factoryTotal)}</strong>
      </div>

      <div className="output-area-sections">
        {(factoryConfig?.areas || []).map(areaConfig => (
          <AreaSection
            key={areaConfig.name}
            areaConfig={areaConfig}
            onSelectStation={setSelectedDetail}
          />
        ))}
      </div>

      <OutputDetailModal
        detail={selectedDetail}
        factoryName={factoryConfig?.name || ''}
        onHide={() => setSelectedDetail(null)}
      />
    </div>
  );
}

OutputPerformanceContent.propTypes = {
  date: PropTypes.string
};

export default function OutputPerformanceTab({ date }) {
  return (
    <OutputDataProvider date={date}>
      <OutputPerformanceContent date={date} />
    </OutputDataProvider>
  );
}

OutputPerformanceTab.propTypes = {
  date: PropTypes.string
};
